import { ButtonRounded, Font, Indicator, Modal } from 'is24-corecss';
import { useEffect, useId, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useBodyScroll } from '@/customHooks/useBodyScroll.hook';
import { useElementDarkMode } from '@/pages/tenant/hooks/shared/useElementDarkMode';
import styles from './ADACUpsellModal.styles';
import adacPartnerLogoWebp from './images/adac-partner-logo.webp';
import type { PlanType } from '@/components/content/welcomeModal/adacUpsell/model';
import mockADACBenefitsData from '@/components/content/welcomeModal/adacUpsell/mock';
import { Benefits } from '@/components/content/welcomeModal/adacUpsell/Benefits';
import { ProductTiles } from '@/components/content/welcomeModal/adacUpsell/ProductTiles';
import { useModalFocus } from '@/customHooks/useModalFocus.hook';
import { useHasMeinPlus } from '@/customHooks/useHasMeinPlus.hook';
import { report } from '@/tracking/tracking';
import { TrackingEvents } from '@/tracking/trackingEvents';

interface ADACUpsellModalProps {
  onDismiss: () => void;
}

export const ADACUpsellModal = ({ onDismiss }: ADACUpsellModalProps) => {
  const visible = true;
  const { t } = useTranslation();
  const isMeinPlus = useHasMeinPlus();
  const hasReportedPageView = useRef(false);
  const { data } = useADACUpsellModalData();
  const [selectedProduct, setSelectedProduct] = useState<PlanType>(data.defaultPlan);
  const titleId = useId();

  useBodyScroll(visible);
  useModalFocus(visible);
  useElementDarkMode('[data-testid="modal-backdrop"]', visible);

  useEffect(() => {
    setSelectedProduct(data.defaultPlan);
  }, [data.defaultPlan]);

  useEffect(() => {
    if (!visible) {
      hasReportedPageView.current = false;
      return;
    }
    if (!hasReportedPageView.current) {
      hasReportedPageView.current = true;
      report(TrackingEvents.ADAC_UPSELL.PAGE_VISITED, isMeinPlus);
    }
  }, [visible, isMeinPlus]);

  const learnMore = () => {
    report({ ...TrackingEvents.ADAC_UPSELL.LEARN_MORE_CLICKED, parameter2: selectedProduct }, isMeinPlus);
    // FIXME: Open the discounted Wohnen+ ADAC Quick Cart.
  };
  const continueToPayment = () => {
    report({ ...TrackingEvents.ADAC_UPSELL.CONTINUE_TO_PAYMENT_CLICKED, parameter2: selectedProduct }, isMeinPlus);
    // FIXME: Open the Wohnen+ ADAC Checkout with the selected billing interval.
  };

  const dismiss = () => {
    report(TrackingEvents.ADAC_UPSELL.MODAL_CLOSE, isMeinPlus);
    onDismiss();
  };

  const onProductSelect = (productId: PlanType) => {
    if (productId === selectedProduct) return;
    report({ ...TrackingEvents.ADAC_UPSELL.OPTION_CLICKED, parameter2: `option_adac_${productId}` }, isMeinPlus);
    setSelectedProduct(productId);
  };

  return (
    <Modal.Root isOpen={visible} onClose={dismiss}>
      <Modal.Backdrop>
        <Modal.Surface aria-labelledby={titleId} width={720} className={styles.surface}>
          <Modal.Header>
            <Modal.Title id={titleId} className={`${styles.heading} font-heading-medium-bold`}>
              <TransWithIndicator i18nKey='common.adacModal.product' />
            </Modal.Title>
            <Modal.CloseButton aria-label={t('common.buttons.close')} />
          </Modal.Header>
          <Modal.Body className={`${styles.contentWrapper} padding-none`}>
            <div className={`${styles.body} display-flex gap-l`} data-testid='wohnen-plus-benefit-modal-adac'>
              <div className='padding-horizontal-l palm-padding-horizontal-xl'>
                <Font variant='font-heading-small-bold' className={`${styles.heading} margin-bottom-none`}>
                  {t('common.adacModal.headline')}
                </Font>
                <div
                  className={`${styles.partnership} display-flex grid-align-center gap-m margin-top-m margin-bottom-l`}
                >
                  <Font variant='font-body-large-bold'>{t('common.adacModal.partnership')}</Font>
                  <img
                    className={`${styles.partnerLogo} display-block`}
                    src={adacPartnerLogoWebp.src}
                    srcSet={adacPartnerLogoWebp.srcSet}
                    alt='ADAC Zuhause Versicherung AG'
                    sizes='121px'
                    width={1182}
                    height={284}
                  />
                </div>
                <p className={`${styles.description} font-body-large-regular margin-bottom-l`}>
                  <TransWithIndicator i18nKey='common.adacModal.discount-description' />
                </p>
                <p className={`${styles.description} font-body-large-regular margin-bottom-l`}>
                  {t('common.adacModal.description')}
                </p>
                <Benefits className='margin-bottom-none' />
              </div>
              <div
                className={`${styles.purchaseSection} padding-horizontal-l palm-padding-horizontal-xl padding-top-m palm-padding-top-l`}
              >
                <ProductTiles plans={data.plans} selectedProduct={selectedProduct} onProuctSelect={onProductSelect} />
                <DailyPriceExplanation className='palm-hide margin-top-l' />
                <Modal.Footer sticky={false} className='padding-horizontal-none'>
                  <ButtonRounded
                    size='large'
                    variant='textDefault'
                    className={`${styles.secondaryAction} palm-one-whole`}
                    onClick={learnMore}
                  >
                    {t('common.adacModal.actions.secondary')}
                  </ButtonRounded>
                  <ButtonRounded
                    size='large'
                    className='palm-one-whole'
                    variant='filledBrand'
                    onClick={continueToPayment}
                  >
                    {t('common.adacModal.actions.primary')}
                  </ButtonRounded>
                </Modal.Footer>
              </div>
            </div>
            <DailyPriceExplanation className='lap-hide desk-hide padding-horizontal-xl padding-bottom-l' />
          </Modal.Body>
        </Modal.Surface>
      </Modal.Backdrop>
    </Modal.Root>
  );
};

const DailyPriceExplanation = ({ className }: { className: string }) => {
  const { t } = useTranslation();

  return (
    <p className={`${styles.description} font-body-small-regular margin-bottom-none ${className}`}>
      {t('common.adacModal.daily-price-explanation')}
    </p>
  );
};

const TransWithIndicator = ({ i18nKey }: { i18nKey: string }) => (
  <Trans i18nKey={i18nKey} components={{ Indicator: <Indicator asSpan appearance='plus' />, strong: <strong /> }} />
);

const useADACUpsellModalData = () => {
  return { data: mockADACBenefitsData };
};
